import type { Color, PaletteOptions } from '@mui/material'
import { alpha } from '@mui/material/styles'

export type AppTheme = 'light' | 'dark' | 'highContrast'

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Color
    isHighContrast: boolean
    key: AppTheme
  }

  interface PaletteOptions {
    neutral?: Partial<Color>
    isHighContrast: boolean
    key: AppTheme
  }
}

const neutral = {
  '100': '#F5F5F5',
  '200': '#E0E0E0',
  '300': '#BDBDBD',
  '400': '#9E9E9E',
  '500': '#757575',
  '600': '#616161',
  '700': '#424242',
  '800': '#303030',
  '900': '#212121',
} as const

/**
 * High contrast collapses most of the neutral ramp to pure black so that every
 * surface reads as the same ground. The two steps below black exist because
 * controls still need to separate from that ground: `600` gives switch tracks
 * and table headers something visible to sit on, and `700`/`800` keep the
 * borders that replace shadows in this theme.
 */
const highContrastNeutral = {
  '100': '#FFFFFF', // White
  '200': '#F5F5F5', // Light gray
  '300': '#D3D3D3', // Medium gray
  '400': '#A9A9A9', // Dark gray
  '500': '#000000', // Black — the shared ground
  '600': '#2E2E2E', // Raised control ground (7.6:1 against white foreground)
  '700': '#FFFFFF', // Border / outline
  '800': '#000000',
  '900': '#000000',
} as const

// The brand orange. It works as a *fill* (dark text on it reaches 6.7:1) but not
// as text: on white it is only 2.79:1, so anything that renders primary as text
// or as a 1px outline uses `primary.dark` below instead.
export const primaryLight = '#FF6F00'
export const primaryDark = '#FF6F00'

/** Brand orange darkened until it passes 4.5:1 on white (4.98:1). For text and outlines in the light theme. */
export const primaryTextLight = '#B35400'
/** On dark grounds the brand orange already passes as text (6.5:1), so it needs no darkened partner. */
export const primaryTextDark = primaryDark

export const secondary = '#00BCD4'

// `contrastText` is what renders *on top of* `main`. White on these mid-tone
// fills failed AA badly (success 2.85:1, warning 2.96:1), so the light-on-dark
// pairs are chosen per colour rather than defaulted to white.
const success = { main: '#00B140', contrastText: '#04150A' }
const error = { main: '#B3261E', contrastText: '#FFFFFF' }
const info = { main: '#005EB8', contrastText: '#FFFFFF' }
const warning = { main: '#EA7600', contrastText: '#1A0D00' }

// High contrast runs on pure black, where the semantic colours above sit between
// 3.2:1 and 3.4:1 — below AA for text. These are the same six meanings lifted
// until each clears 7:1 on black.
const highContrastSuccess = { main: '#4ADE80', contrastText: '#000000' }
const highContrastError = { main: '#FF6B6B', contrastText: '#000000' }
const highContrastInfo = { main: '#6BB6FF', contrastText: '#000000' }
const highContrastWarning = { main: '#FFB020', contrastText: '#000000' }

export const lightPalette = {
  mode: 'light',
  key: 'light',
  isHighContrast: false,
  // The grounds are biased a few degrees warm rather than the cool blue-grey
  // they were. Against a warm brand orange a cool canvas reads as two unrelated
  // palettes sharing a screen; the tint is what ties the accent to the surface
  // it sits on. Cards stay pure white so they lift off the tinted page.
  background: { paper: '#FFFFFF', default: '#FAF8F6' },
  action: {
    active: neutral[500],
    focus: 'rgba(0, 0, 0, 0.12)',
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabledBackground: '#E9EAEC',
    disabled: '#666666',
  },
  primary: { main: primaryLight, dark: primaryTextLight },
  secondary: { main: secondary },
  text: { primary: '#333333', secondary: '#4D4D4D' },
  success,
  info,
  warning,
  error,
  divider: '#CDD1D8',
  neutral,
} as const satisfies PaletteOptions

export const darkPalette = {
  mode: 'dark',
  key: 'dark',
  isHighContrast: false,
  // Dark is composed rather than inverted: paper sits a measured step above the
  // canvas so elevation is legible without a border, and both carry the same
  // warm bias as the light theme.
  background: { paper: '#191614', default: '#100E0D' },
  action: {
    active: neutral[400],
    focus: 'rgba(255, 255, 255, 0.12)',
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: alpha(primaryDark, 0.08),
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
    disabled: 'rgba(255, 255, 255, 0.26)',
  },
  primary: { main: primaryDark, dark: primaryTextDark },
  secondary: { main: secondary },
  text: { primary: '#FFFFFF', secondary: '#B3B3B3' },
  success,
  error,
  info,
  warning,
  divider: 'rgba(255, 255, 255, 0.16)',
  neutral,
} as const satisfies PaletteOptions

export const highContrastDarkPalette = {
  mode: 'dark',
  key: 'highContrast',
  isHighContrast: true,
  background: { paper: '#000000', default: '#000000' },
  action: {
    active: neutral[400],
    focus: 'rgba(255, 255, 255, 0.35)',
    hover: 'rgba(255, 255, 255, 0.16)',
    // The theme built for maximum contrast previously had the *weakest* selected
    // state of the three (4% orange on black, ~1.03:1). Selection here is a solid
    // fill, and SideBarListItem adds an accent bar so it is not colour-only.
    selected: alpha(primaryDark, 0.28),
    disabledBackground: 'rgba(255, 255, 255, 0.2)',
    disabled: 'rgba(255, 255, 255, 0.5)',
  },
  primary: { main: primaryDark, dark: primaryTextDark },
  secondary: { main: secondary },
  text: { primary: '#FFFFFF', secondary: '#FFFFFF' },
  success: highContrastSuccess,
  error: highContrastError,
  info: highContrastInfo,
  warning: highContrastWarning,
  divider: '#FFFFFF',
  neutral: highContrastNeutral,
} as const satisfies PaletteOptions
